/**
 * @file Login.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-09-21
 * @modified 2025-09-21
 */

import { useState, useEffect } from 'react';
import './Login.css';

function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [theme, setTheme] = useState('dark');
  /*
   * The same card, asking a different question. A separate page for this would
   * be a route to maintain and a place to get stranded; the email is already
   * typed in by the time anyone realises they cannot remember the password.
   */
  const [mode, setMode] = useState('signin');   // 'signin' | 'forgot'
  const [notice, setNotice] = useState('');

  useEffect(() => {
      const savedTheme = localStorage.getItem('theme') || 'dark';
      setTheme(savedTheme);
      document.documentElement.setAttribute('data-theme', savedTheme);
    }, []);

  /*
   * Ask for a reset link.
   *
   * The server answers the same whether or not the address has an account, and
   * so does this — a screen that said "no such user" would undo the point of
   * that. The wording says "if that address has an account" so a typo does not
   * read as success.
   */
  const requestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setNotice('');
    try {
      const response = await fetch('/api/users/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 429) {
        setError(data.message || 'Too many requests. Please try again shortly.');
      } else {
        setNotice(data.message
          || 'If that address has an account, a reset link is on its way.');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (next) => {
    setMode(next);
    setError('');
    setNotice('');
    setPassword('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch('/api/users/login', {  
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        onLogin(data.user);  // Pass user instead of token
      } else {
        setError(data.error || 'Login failed');
      }

    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container svg-bg">
      <div className="login-card">
        <div className="login-header">
          <img src={theme === 'light' ? '/excel-agri-logo-dark.svg' : '/excel-agri-logo.svg'} alt="Excel Agri" className="login-logo-icon" />
        </div>
        
        <form className="login-form" onSubmit={mode === 'forgot' ? requestReset : handleSubmit}>
          {error && <div className="error-message">{error}</div>}
          {notice && <div className="notice-message">{notice}</div>}

          {mode === 'forgot' && !notice && (
            <p className="forgot-intro">
              Enter the address you sign in with and we will send you a link to set
              a new password. The link lasts an hour.
            </p>
          )}
          
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-input"
              placeholder="name@excel-agri.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          
          {mode === 'signin' && (
            <div className="form-group">
              <label className="form-label">Password</label>
              <input
                type="password"
                className="form-input"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
          )}

          <div className="forgot-password">
            <button type="button" className="link-button"
                    onClick={() => switchMode(mode === 'signin' ? 'forgot' : 'signin')}>
              {mode === 'signin' ? 'Forgot Password?' : 'Back to sign in'}
            </button>
          </div>

          {/* Once the link is away there is nothing more to do here, so the
              button goes rather than inviting a second identical request. */}
          {!(mode === 'forgot' && notice) && (
            <button
              type="submit"
              className="btn btn-primary signin-button"
              disabled={loading}
            >
              {loading
                ? (mode === 'forgot' ? 'Sending…' : 'Signing In...')
                : (mode === 'forgot' ? 'Send reset link' : 'Sign In')}
            </button>
          )}
        </form>
        
        <div className="signup-link">
          Don't have an account? <a href="/signup">Create one</a>
        </div>
      </div>
    </div>
  );
}

export default Login;